import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaUsers, FaUserPlus, FaFileUpload, FaEnvelope, FaChartLine, FaDownload,
  FaCheck, FaTimes, FaSpinner, FaSearch, FaFilter, FaEye, FaPrint,
  FaStar, FaExclamationTriangle, FaArrowUp, FaArrowDown, FaClock,
  FaPaperPlane, FaFileAlt, FaSchool, FaUserGraduate, FaEdit, FaBook
} from 'react-icons/fa';
import AppHeader from '../components/AppHeader';
import api from '../utils/api';
import { toast } from 'sonner';

// Fonction pour télécharger un fichier PDF avec authentification
const downloadPDF = async (endpoint, filename) => {
  try {
    const response = await api.get(endpoint, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    toast.success('Guide téléchargé !');
  } catch (error) {
    console.error('Erreur téléchargement:', error);
    toast.error('Erreur lors du téléchargement');
  }
};

// Score badge component
const ScoreBadge = ({ score, size = 'normal' }) => {
  let bgColor = '#EF4444'; // Rouge
  if (score >= 70) bgColor = '#10B981'; // Vert
  else if (score >= 55) bgColor = '#F59E0B'; // Orange
  
  const sizeClasses = size === 'small' ? 'w-8 h-8 text-xs' : 'w-12 h-12 text-sm';
  
  return (
    <div 
      className={`${sizeClasses} rounded-full flex items-center justify-center text-white font-bold`}
      style={{ backgroundColor: bgColor }}
    >
      {Math.round(score)}
    </div>
  );
};

// Student row component
const StudentRow = ({ student, onViewDetails, onSendInvitation }) => {
  const [showDetails, setShowDetails] = useState(false);
  
  return (
    <>
      <tr className="hover:bg-gray-50 border-b">
        <td className="px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
              {student.firstName[0]}{student.lastName[0]}
            </div>
            <div>
              <p className="font-semibold">{student.firstName} {student.lastName}</p>
              <p className="text-xs text-gray-500">{student.email}</p>
            </div>
          </div>
        </td>
        <td className="px-4 py-3 text-center">
          <span className="px-2 py-1 bg-gray-100 rounded text-sm">{student.cohort}</span>
        </td>
        <td className="px-4 py-3 text-center">
          <ScoreBadge score={student.globalScore} />
        </td>
        <td className="px-4 py-3 text-center">
          <div className="flex items-center justify-center gap-1">
            <FaClock className="text-gray-400 text-xs" />
            <span className={student.streakDays > 0 ? 'text-green-600' : 'text-gray-400'}>
              {student.totalDaysActive}j
            </span>
            {student.streakDays > 0 && (
              <span className="text-xs text-green-500 ml-1">🔥{student.streakDays}</span>
            )}
          </div>
        </td>
        <td className="px-4 py-3">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full" 
              style={{ width: `${student.progressPercent}%` }}
            />
          </div>
          <span className="text-xs text-gray-500">{student.progressPercent}%</span>
        </td>
        <td className="px-4 py-3 text-center">
          <div className="flex gap-2 justify-center">
            <button 
              onClick={() => onViewDetails(student)}
              className="p-2 text-blue-600 hover:bg-blue-50 rounded"
              title="Voir détails"
            >
              <FaEye />
            </button>
            <button 
              onClick={() => setShowDetails(!showDetails)}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded"
              title="Détails rapides"
            >
              <FaChartLine />
            </button>
          </div>
        </td>
      </tr>
      {showDetails && (
        <tr className="bg-gray-50">
          <td colSpan="6" className="px-6 py-4">
            <div className="grid grid-cols-7 gap-4 text-center text-sm">
              <div>
                <p className="text-gray-500 text-xs">Quiz</p>
                <ScoreBadge score={student.quizScore} size="small" />
              </div>
              <div>
                <p className="text-gray-500 text-xs">Grammaire</p>
                <ScoreBadge score={student.grammarScore} size="small" />
              </div>
              <div>
                <p className="text-gray-500 text-xs">SEO</p>
                <ScoreBadge score={student.seoScore} size="small" />
              </div>
              <div>
                <p className="text-gray-500 text-xs">Interview</p>
                <ScoreBadge score={student.interviewScore} size="small" />
              </div>
              <div>
                <p className="text-gray-500 text-xs">Pitch</p>
                <ScoreBadge score={student.pitchScore} size="small" />
              </div>
              <div>
                <p className="text-gray-500 text-xs">Dactylo</p>
                <ScoreBadge score={student.typingScore} size="small" />
              </div>
              <div>
                <p className="text-gray-500 text-xs">Qui est qui</p>
                <ScoreBadge score={student.whoIsWhoScore} size="small" />
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// Modal pour les détails de l'élève
const StudentDetailModal = ({ student, onClose, onGenerateReport }) => {
  const [details, setDetails] = useState(null);
  const [grades, setGrades] = useState(null);
  const [evolution, setEvolution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teacherComment, setTeacherComment] = useState('');
  const [generating, setGenerating] = useState(false);
  const [selectedTrimester, setSelectedTrimester] = useState(2);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'grades', 'evolution'
  
  useEffect(() => {
    loadAllData();
  }, [student.id, selectedTrimester]);
  
  const loadAllData = async () => {
    setLoading(true);
    try {
      const [detailsRes, gradesRes, evolutionRes] = await Promise.all([
        api.get(`/educator/student/${student.id}`),
        api.get(`/educator/student/${student.id}/grades?trimester=${selectedTrimester}`),
        api.get(`/educator/student/${student.id}/evolution`)
      ]);
      setDetails(detailsRes.data);
      setGrades(gradesRes.data);
      setEvolution(evolutionRes.data);
    } catch (error) {
      console.error('Error loading student data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleGenerateReport = async () => {
    setGenerating(true);
    try {
      const response = await api.post('/educator/report-card/generate-v2', {
        studentId: student.id,
        teacherComment: teacherComment,
        trimester: selectedTrimester,
        includeExternalGrades: true
      });
      onGenerateReport(response.data);
    } catch (error) {
      console.error('Error generating report:', error);
    } finally {
      setGenerating(false);
    }
  };
  
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <FaSpinner className="animate-spin text-4xl text-blue-600 mx-auto" />
        </div>
      </div>
    );
  }
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white p-6 rounded-t-xl">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold">{student.firstName} {student.lastName}</h2>
              <p className="opacity-80">{student.email}</p>
              <p className="text-sm mt-1">{student.cohort}</p>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white text-2xl">
              <FaTimes />
            </button>
          </div>
          
          {/* 3 Moyennes */}
          {grades?.averages && (
            <div className="flex gap-6 mt-4">
              <div className="text-center bg-white/10 rounded-lg px-4 py-2">
                <p className="text-3xl font-bold">{grades.averages.dailyGlobe}</p>
                <p className="text-xs opacity-80">DailyGlobe</p>
              </div>
              <div className="text-center bg-white/10 rounded-lg px-4 py-2">
                <p className="text-3xl font-bold">{grades.averages.external || '-'}</p>
                <p className="text-xs opacity-80">Matières externes</p>
              </div>
              <div className="text-center bg-yellow-400 text-blue-900 rounded-lg px-4 py-2">
                <p className="text-3xl font-bold">{grades.averages.combined}</p>
                <p className="text-xs">Moyenne générale</p>
              </div>
              <div className="text-center bg-white/10 rounded-lg px-4 py-2">
                <p className="text-3xl font-bold">{student.totalDaysActive}</p>
                <p className="text-xs opacity-80">Jours actifs</p>
              </div>
              <div className="text-center bg-white/10 rounded-lg px-4 py-2">
                <p className="text-3xl font-bold">{student.streakDays}🔥</p>
                <p className="text-xs opacity-80">Série</p>
              </div>
            </div>
          )}
          
          {/* Trimester selector */}
          <div className="flex gap-2 mt-4">
            {[1, 2, 3].map(t => (
              <button
                key={t}
                onClick={() => setSelectedTrimester(t)}
                className={`px-3 py-1 rounded text-sm ${
                  selectedTrimester === t 
                    ? 'bg-white text-blue-900 font-bold' 
                    : 'bg-white/20 hover:bg-white/30'
                }`}
              >
                {t === 1 ? '1er' : `${t}ème`} trimestre
              </button>
            ))}
          </div>
        </div>
        
        {/* Tabs */}
        <div className="border-b px-6">
          <div className="flex gap-4">
            {[
              { id: 'overview', label: 'Vue d\'ensemble', icon: FaChartLine },
              { id: 'grades', label: 'Notes & Matières', icon: FaStar },
              { id: 'evolution', label: 'Évolution', icon: FaArrowUp }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-3 px-2 border-b-2 transition-colors ${
                  activeTab === tab.id 
                    ? 'border-blue-600 text-blue-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="text-sm" /> {tab.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6">
          {/* Tab: Overview */}
          {activeTab === 'overview' && (
            <>
              {/* Forces et faiblesses */}
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="font-bold text-green-800 mb-2 flex items-center gap-2">
                    <FaArrowUp /> Points forts
                  </h3>
                  <ul className="space-y-1">
                    {details?.strengths?.map((s, i) => (
                      <li key={i} className="text-green-700 text-sm flex items-center gap-2">
                        <FaCheck className="text-green-500" /> {s}
                      </li>
                    ))}
                    {(!details?.strengths || details.strengths.length === 0) && (
                      <li className="text-gray-500 text-sm">Aucun point fort identifié pour le moment</li>
                    )}
                  </ul>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="font-bold text-red-800 mb-2 flex items-center gap-2">
                    <FaArrowDown /> Axes d'amélioration
                  </h3>
                  <ul className="space-y-1">
                    {details?.weaknesses?.map((w, i) => (
                      <li key={i} className="text-red-700 text-sm flex items-center gap-2">
                        <FaExclamationTriangle className="text-red-500" /> {w}
                      </li>
                    ))}
                    {(!details?.weaknesses || details.weaknesses.length === 0) && (
                      <li className="text-gray-500 text-sm">Aucune faiblesse majeure identifiée</li>
                    )}
                  </ul>
                </div>
              </div>
              
              {/* Commentaire global IA */}
              <div className="bg-blue-50 p-4 rounded-lg mb-6">
                <h3 className="font-bold text-blue-800 mb-2">Analyse globale</h3>
                <p className="text-blue-900 text-sm leading-relaxed">
                  {details?.aiGlobalComment || "Chargement de l'analyse..."}
                </p>
              </div>
              
              {/* Détails par matière DailyGlobe */}
              <h3 className="font-bold text-gray-800 mb-3">Ateliers DailyGlobe</h3>
              <div className="grid gap-3 mb-6">
                {details?.aiComments && Object.entries(details.aiComments).map(([key, comment]) => {
                  const labels = {
                    quiz: "Quiz d'actualité",
                    grammar: "Grammaire & Orthographe",
                    seo: "SEO",
                    interview: "Simulation d'interview",
                    pitch: "Pitch d'angles",
                    typing: "Dactylographie",
                    whoIsWho: "Qui est qui ?"
                  };
                  const scores = {
                    quiz: student.quizScore,
                    grammar: student.grammarScore,
                    seo: student.seoScore,
                    interview: student.interviewScore,
                    pitch: student.pitchScore,
                    typing: student.typingScore,
                    whoIsWho: student.whoIsWhoScore
                  };
                  
                  return (
                    <div key={key} className="bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium">{labels[key] || key}</span>
                        <ScoreBadge score={scores[key] || 0} size="small" />
                      </div>
                      <p className="text-sm text-gray-600">{comment}</p>
                    </div>
                  );
                })}
              </div>
            </>
          )}
          
          {/* Tab: Grades */}
          {activeTab === 'grades' && grades && (
            <>
              {/* Notes DailyGlobe */}
              <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                <FaUserGraduate className="text-blue-600" /> Ateliers DailyGlobe
                <span className="text-sm font-normal text-gray-500">
                  Moyenne: {grades.averages.dailyGlobe}/100
                </span>
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {Object.entries(grades.dailyGlobeGrades || {}).map(([key, data]) => {
                  const labels = {
                    quiz: "Quiz",
                    grammar: "Grammaire",
                    seo: "SEO",
                    interview: "Interview",
                    pitch: "Pitch",
                    typing: "Dactylo",
                    whoIsWho: "Qui est qui"
                  };
                  return (
                    <div key={key} className="bg-blue-50 p-3 rounded-lg text-center">
                      <p className="text-xs text-gray-600 mb-1">{labels[key] || key}</p>
                      <p className="text-2xl font-bold text-blue-800">{Math.round(data.score)}</p>
                      <p className="text-xs text-gray-500">{data.attempts} exercices</p>
                    </div>
                  );
                })}
              </div>
              
              {/* Notes Matières Externes */}
              <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                <FaSchool className="text-green-600" /> Matières complémentaires
                <span className="text-sm font-normal text-gray-500">
                  Moyenne: {grades.averages.external || '-'}/100
                </span>
              </h3>
              {grades.externalGrades && grades.externalGrades.length > 0 ? (
                <div className="bg-white border rounded-lg overflow-hidden mb-6">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left">Matière</th>
                        <th className="px-4 py-2 text-center">Coefficient</th>
                        <th className="px-4 py-2 text-center">Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {grades.externalGrades.map((g, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-4 py-2 font-medium">{g.subjectName}</td>
                          <td className="px-4 py-2 text-center text-gray-500">{g.coefficient}</td>
                          <td className="px-4 py-2 text-center">
                            {g.grade !== null ? (
                              <ScoreBadge score={g.grade} size="small" />
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="bg-gray-50 p-4 rounded-lg mb-6 text-gray-500 text-center">
                  Aucune note externe pour ce trimestre
                </div>
              )}
              
              {/* Résumé des 3 moyennes */}
              <div className="bg-gradient-to-r from-blue-100 to-green-100 p-4 rounded-lg">
                <h3 className="font-bold text-gray-800 mb-3">Récapitulatif des moyennes</h3>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-3xl font-bold text-blue-700">{grades.averages.dailyGlobe}</p>
                    <p className="text-sm text-gray-600">DailyGlobe</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-green-700">{grades.averages.external || '-'}</p>
                    <p className="text-sm text-gray-600">Matières ext.</p>
                  </div>
                  <div className="bg-white rounded-lg p-2">
                    <p className="text-3xl font-bold text-gray-800">{grades.averages.combined}</p>
                    <p className="text-sm text-gray-600 font-medium">Générale</p>
                  </div>
                </div>
              </div>
            </>
          )}
          
          {/* Tab: Evolution */}
          {activeTab === 'evolution' && evolution && (
            <>
              {/* Analyse IA de l'évolution */}
              <div className="bg-purple-50 p-4 rounded-lg mb-6">
                <h3 className="font-bold text-purple-800 mb-2 flex items-center gap-2">
                  <FaChartLine /> Analyse de l'évolution
                </h3>
                <p className="text-purple-900 text-sm leading-relaxed">
                  {evolution.evolutionAnalysis}
                </p>
              </div>
              
              {/* Graphique de progression */}
              <h3 className="font-bold text-gray-800 mb-3">Progression par trimestre</h3>
              <div className="grid grid-cols-3 gap-4 mb-6">
                {[1, 2, 3].map(t => {
                  const dgScore = evolution.history?.dailyGlobe?.[t];
                  const extScore = evolution.history?.external?.[t];
                  const combined = evolution.history?.combined?.[t];
                  const isCurrent = t === evolution.currentTrimester;
                  
                  return (
                    <div 
                      key={t} 
                      className={`p-4 rounded-lg border-2 ${
                        isCurrent ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                      }`}
                    >
                      <h4 className="font-bold text-center mb-3">
                        {t === 1 ? '1er' : `${t}ème`} trimestre
                        {isCurrent && <span className="text-blue-600 text-xs ml-1">(actuel)</span>}
                      </h4>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">DailyGlobe:</span>
                          <span className="font-bold">{dgScore || '-'}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Externe:</span>
                          <span className="font-bold">{extScore || '-'}</span>
                        </div>
                        <div className="flex justify-between text-sm border-t pt-2">
                          <span className="text-gray-800 font-medium">Combiné:</span>
                          <span className="font-bold text-lg">{combined || '-'}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Évolution visuelle */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-bold text-gray-700 mb-3">Évolution de la moyenne générale</h4>
                <div className="flex items-end justify-around h-32 gap-2">
                  {[1, 2, 3].map(t => {
                    const score = evolution.history?.combined?.[t] || 0;
                    const height = score > 0 ? (score / 100) * 100 : 10;
                    const prevScore = t > 1 ? (evolution.history?.combined?.[t-1] || 0) : 0;
                    const diff = score - prevScore;
                    
                    return (
                      <div key={t} className="flex flex-col items-center flex-1">
                        <div 
                          className={`w-full max-w-16 rounded-t transition-all ${
                            score >= 70 ? 'bg-green-500' : score >= 55 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ height: `${height}%` }}
                        />
                        <p className="text-sm font-bold mt-1">{score || '-'}</p>
                        <p className="text-xs text-gray-500">T{t}</p>
                        {t > 1 && prevScore > 0 && score > 0 && (
                          <p className={`text-xs ${diff >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {diff >= 0 ? '+' : ''}{diff.toFixed(1)}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
          
          {/* Générer bulletin */}
          <div className="border-t pt-4 mt-6">
            <h3 className="font-bold text-gray-800 mb-3">Générer le bulletin du {selectedTrimester === 1 ? '1er' : `${selectedTrimester}ème`} trimestre</h3>
            <textarea
              value={teacherComment}
              onChange={(e) => setTeacherComment(e.target.value)}
              placeholder="Ajoutez votre commentaire personnel pour l'élève..."
              className="w-full p-3 border rounded-lg mb-3 h-24"
            />
            <div className="flex gap-3">
              <button
                onClick={handleGenerateReport}
                disabled={generating}
                className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 flex items-center justify-center gap-2"
                data-testid="generate-report-btn"
              >
                {generating ? (
                  <><FaSpinner className="animate-spin" /> Génération...</>
                ) : (
                  <><FaFileAlt /> Générer le bulletin complet</>
                )}
              </button>
            </div>
            <p className="text-xs text-gray-500 text-center mt-2">
              Le bulletin inclura les notes DailyGlobe, les matières externes, les 3 moyennes et l'analyse IA
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Modal d'inscription élève
const EnrollStudentModal = ({ onClose, onEnroll }) => {
  const [mode, setMode] = useState('manual'); // 'manual' or 'file'
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', cohort: '' });
  const [fileStudents, setFileStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const handleManualSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/educator/student/enroll', formData);
      onEnroll([{ ...formData, id: response.data.student.id, tempPassword: response.data.tempPassword }]);
      onClose();
    } catch (error) {
      alert(error.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setLoading(false);
    }
  };
  
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/educator/students/parse-file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setFileStudents(response.data.students || []);
    } catch (error) {
      alert(error.response?.data?.detail || "Erreur lors de l'analyse du fichier");
    } finally {
      setUploading(false);
    }
  };
  
  const handleBulkEnroll = async () => {
    setLoading(true);
    try {
      const response = await api.post('/educator/students/bulk-enroll', { students: fileStudents });
      onEnroll(response.data.success);
      if (response.data.errors.length > 0) {
        alert(`${response.data.errors.length} erreur(s) lors de l'inscription`);
      }
      onClose();
    } catch (error) {
      alert(error.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold">Inscrire des élèves</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <FaTimes />
            </button>
          </div>
          
          {/* Mode selector */}
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => setMode('manual')}
              className={`flex-1 py-2 px-4 rounded-lg font-medium ${
                mode === 'manual' ? 'bg-blue-600 text-white' : 'bg-gray-100'
              }`}
            >
              <FaUserPlus className="inline mr-2" /> Manuel
            </button>
            <button
              onClick={() => setMode('file')}
              className={`flex-1 py-2 px-4 rounded-lg font-medium ${
                mode === 'file' ? 'bg-blue-600 text-white' : 'bg-gray-100'
              }`}
            >
              <FaFileUpload className="inline mr-2" /> Import fichier
            </button>
          </div>
        </div>
        
        <div className="p-6">
          {mode === 'manual' ? (
            <form onSubmit={handleManualSubmit}>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Prénom *</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full p-3 border rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Nom *</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="w-full p-3 border rounded-lg"
                    required
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full p-3 border rounded-lg"
                  required
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium mb-1">Classe / Promotion</label>
                <input
                  type="text"
                  value={formData.cohort}
                  onChange={(e) => setFormData({ ...formData, cohort: e.target.value })}
                  className="w-full p-3 border rounded-lg"
                  placeholder="Ex: Promo 2026"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 flex items-center justify-center gap-2"
              >
                {loading ? <FaSpinner className="animate-spin" /> : <FaUserPlus />}
                Inscrire l'élève
              </button>
            </form>
          ) : (
            <div>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center mb-4">
                <FaFileUpload className="text-4xl text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 mb-2">Glissez votre fichier ici ou</p>
                <label className="cursor-pointer">
                  <span className="text-blue-600 hover:underline">parcourez vos fichiers</span>
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls,.doc,.docx,.txt"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </label>
                <p className="text-xs text-gray-400 mt-2">Formats acceptés : CSV, Excel, Word</p>
              </div>
              
              {uploading && (
                <div className="text-center py-4">
                  <FaSpinner className="animate-spin text-2xl text-blue-600 mx-auto" />
                  <p className="text-gray-600 mt-2">Analyse du fichier en cours...</p>
                </div>
              )}
              
              {fileStudents.length > 0 && (
                <div>
                  <h3 className="font-bold mb-2">{fileStudents.length} élèves détectés</h3>
                  <div className="max-h-60 overflow-y-auto border rounded-lg mb-4">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left">Prénom</th>
                          <th className="px-3 py-2 text-left">Nom</th>
                          <th className="px-3 py-2 text-left">Email</th>
                          <th className="px-3 py-2 text-left">Classe</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fileStudents.map((s, i) => (
                          <tr key={i} className="border-t">
                            <td className="px-3 py-2">{s.firstName}</td>
                            <td className="px-3 py-2">{s.lastName}</td>
                            <td className="px-3 py-2">{s.email}</td>
                            <td className="px-3 py-2">{s.cohort}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button
                    onClick={handleBulkEnroll}
                    disabled={loading}
                    className="w-full bg-green-600 text-white py-3 rounded-lg font-bold hover:bg-green-700 flex items-center justify-center gap-2"
                  >
                    {loading ? <FaSpinner className="animate-spin" /> : <FaCheck />}
                    Valider et inscrire {fileStudents.length} élèves
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

// Composant principal
function EducatorDashboardPage() {
  const [students, setStudents] = useState([]);
  const [schoolInfo, setSchoolInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [cohortFilter, setCohortFilter] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [sendingAll, setSendingAll] = useState(false);
  const [reportCard, setReportCard] = useState(null);
  
  useEffect(() => {
    loadStudents();
  }, []);
  
  const loadStudents = async () => {
    try {
      const response = await api.get('/educator/students');
      setStudents(response.data.students || []);
      setSchoolInfo(response.data.schoolInfo || {});
    } catch (error) {
      console.error('Error loading students:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const filteredStudents = students.filter(s => {
    const matchesSearch = `${s.firstName} ${s.lastName} ${s.email}`.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCohort = !cohortFilter || s.cohort === cohortFilter;
    return matchesSearch && matchesCohort;
  });
  
  const handleSendAllReports = async () => {
    if (!window.confirm(`Envoyer les bulletins aux ${filteredStudents.length} élèves affichés ?`)) return;
    
    setSendingAll(true);
    try {
      const studentIds = filteredStudents.map(s => s.id);
      const response = await api.post('/educator/report-cards/send-all', {
        studentIds,
        teacherComment: '',
        trimester: ''
      });
      alert(`${response.data.success.length} bulletins envoyés avec succès !`);
    } catch (error) {
      alert('Erreur lors de l\'envoi des bulletins');
    } finally {
      setSendingAll(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen" style={{ background: '#F8F4E3' }}>
        <AppHeader />
        <div className="flex items-center justify-center h-[60vh]">
          <FaSpinner className="animate-spin text-4xl text-blue-600" />
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen" style={{ background: '#F8F4E3' }}>
      <AppHeader />
      
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <FaSchool className="text-blue-600" />
              {schoolInfo.name || "Tableau de bord"}
            </h1>
            <p className="text-gray-600">{schoolInfo.totalStudents || 0} élèves inscrits</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setShowEnrollModal(true)}
              className="bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 flex items-center gap-2"
            >
              <FaUserPlus /> Inscrire des élèves
            </button>
            <button
              onClick={handleSendAllReports}
              disabled={sendingAll}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 flex items-center gap-2"
            >
              {sendingAll ? <FaSpinner className="animate-spin" /> : <FaPaperPlane />}
              Envoyer tous les bulletins
            </button>
            <button
              onClick={() => downloadPDF('/guides/teacher', 'Guide_Enseignant_DailyGlobe.pdf')}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-purple-700 flex items-center gap-2"
              title="Télécharger le guide de l'enseignant"
            >
              <FaBook /> Guide
            </button>
          </div>
        </div>
        
        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <FaSearch className="absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher un élève..."
                className="w-full pl-10 pr-4 py-2 border rounded-lg"
              />
            </div>
            <div className="flex items-center gap-2">
              <FaFilter className="text-gray-400" />
              <select
                value={cohortFilter}
                onChange={(e) => setCohortFilter(e.target.value)}
                className="border rounded-lg px-3 py-2"
              >
                <option value="">Toutes les classes</option>
                {schoolInfo.cohorts?.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        
        {/* Students table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Élève</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-600">Classe</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-600">Score</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-600">Assiduité</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-600">Progression</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredStudents.map(student => (
                <StudentRow 
                  key={student.id} 
                  student={student}
                  onViewDetails={(s) => setSelectedStudent(s)}
                  onSendInvitation={() => {}}
                />
              ))}
            </tbody>
          </table>
          
          {filteredStudents.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <FaUsers className="text-4xl mx-auto mb-3 opacity-50" />
              <p>Aucun élève trouvé</p>
            </div>
          )}
        </div>
        
        {/* Mode démo indicator */}
        <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center text-sm text-yellow-800">
          <FaExclamationTriangle className="inline mr-2" />
          Mode démonstration : Les données affichées sont simulées pour illustrer les fonctionnalités.
        </div>
      </div>
      
      {/* Modals */}
      {selectedStudent && (
        <StudentDetailModal
          student={selectedStudent}
          onClose={() => setSelectedStudent(null)}
          onGenerateReport={(report) => {
            setReportCard(report);
            setSelectedStudent(null);
          }}
        />
      )}
      
      {showEnrollModal && (
        <EnrollStudentModal
          onClose={() => setShowEnrollModal(false)}
          onEnroll={(newStudents) => {
            // En mode démo, juste afficher un message
            alert(`${newStudents.length} élève(s) inscrit(s) avec succès !`);
          }}
        />
      )}
      
      {/* Report card preview modal */}
      {reportCard && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            {/* Header du bulletin */}
            <div className="p-6 border-b bg-gradient-to-r from-blue-50 to-white">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-bold text-blue-900">
                    {reportCard.schoolName || "École de Journalisme"}
                  </h2>
                  {reportCard.schoolAddress && (
                    <p className="text-sm text-gray-600">{reportCard.schoolAddress}</p>
                  )}
                </div>
                <button onClick={() => setReportCard(null)} className="text-gray-500 hover:text-gray-700">
                  <FaTimes />
                </button>
              </div>
              <div className="mt-4 text-center">
                <h3 className="text-lg font-bold">{reportCard.reportLabel || `Bulletin du ${reportCard.trimesterLabel}`}</h3>
                <p className="text-gray-600">Année scolaire {reportCard.schoolYear}</p>
              </div>
            </div>
            
            <div className="p-6">
              {/* Info élève */}
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-lg font-bold">{reportCard.studentName}</p>
                    <p className="text-sm text-gray-600">{reportCard.cohort}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Généré le</p>
                    <p className="font-medium">{new Date(reportCard.generatedAt).toLocaleDateString('fr-FR')}</p>
                  </div>
                </div>
              </div>
              
              {/* 3 Moyennes */}
              {reportCard.averages && (
                <div className="bg-gradient-to-r from-blue-100 via-green-100 to-yellow-100 p-4 rounded-lg mb-6">
                  <h4 className="font-bold text-gray-800 mb-3 text-center">Moyennes du trimestre</h4>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-3xl font-bold text-blue-700">{reportCard.averages.dailyGlobe}</p>
                      <p className="text-xs text-gray-600">Ateliers DailyGlobe</p>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-3xl font-bold text-green-700">{reportCard.averages.external || '-'}</p>
                      <p className="text-xs text-gray-600">Matières complémentaires</p>
                    </div>
                    <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-3">
                      <p className="text-3xl font-bold text-gray-800">{reportCard.averages.combined}</p>
                      <p className="text-xs text-gray-700 font-medium">Moyenne générale</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Notes externes si présentes */}
              {reportCard.externalGrades && reportCard.externalGrades.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-bold text-gray-800 mb-2">Matières complémentaires</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {reportCard.externalGrades.map((g, i) => (
                      <div key={i} className="flex justify-between bg-gray-50 p-2 rounded text-sm">
                        <span>{g.subjectName}</span>
                        <span className="font-bold">{g.grade}/100</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Appréciation générale */}
              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <h4 className="font-bold text-blue-800 mb-2">Appréciation générale</h4>
                <p className="text-sm">{reportCard.aiGlobalComment}</p>
              </div>
              
              {/* Évolution */}
              {reportCard.evolutionAnalysis && (
                <div className="bg-purple-50 p-4 rounded-lg mb-4">
                  <h4 className="font-bold text-purple-800 mb-2">Évolution</h4>
                  <p className="text-sm">{reportCard.evolutionAnalysis}</p>
                </div>
              )}
              
              {/* Commentaire du professeur */}
              {reportCard.teacherComment && (
                <div className="bg-green-50 p-4 rounded-lg mb-4">
                  <h4 className="font-bold text-green-800 mb-2">Commentaire du professeur</h4>
                  <p className="text-sm">{reportCard.teacherComment}</p>
                </div>
              )}
              
              {/* Signature */}
              <div className="flex justify-between items-end mt-6 pt-4 border-t">
                <div>
                  <p className="text-sm text-gray-500">Le directeur,</p>
                  <p className="font-bold">{reportCard.directorName}</p>
                  <p className="text-sm text-gray-600">{reportCard.directorFunction}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Date</p>
                  <p className="font-medium">{new Date().toLocaleDateString('fr-FR')}</p>
                </div>
              </div>
              
              {/* Mention légale */}
              <div className="mt-6 pt-4 border-t text-center">
                <p className="text-xs text-gray-500 italic">
                  {reportCard.legalNotice || "Conservez précieusement ce relevé de notes pendant toute la durée de votre scolarité."}
                </p>
              </div>
              
              {/* Actions */}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={async () => {
                    try {
                      await api.post(`/educator/report-card/${reportCard.id}/send`);
                      alert('Bulletin envoyé !');
                      setReportCard(null);
                    } catch (e) {
                      alert('Erreur lors de l\'envoi');
                    }
                  }}
                  className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 flex items-center justify-center gap-2"
                  data-testid="send-report-btn"
                >
                  <FaPaperPlane /> Envoyer par email
                </button>
                <button
                  onClick={() => window.print()}
                  className="flex-1 bg-gray-600 text-white py-3 rounded-lg font-bold hover:bg-gray-700 flex items-center justify-center gap-2"
                  data-testid="print-report-btn"
                >
                  <FaPrint /> Imprimer
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

export default EducatorDashboardPage;
